import { NavLink, Outlet } from 'react-router-dom';
import { Badge } from '@/components/Badge/Badge';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Spinner } from '@/components/Spinner/Spinner';
import { isAdmin, useIdentity, usePrincipal } from '@/features/auth/identity';
import styles from './AdminLayout.module.scss';

const SECTIONS = [
  { to: '/admin', end: true, label: 'Overview', hint: 'US-6.1' },
  { to: '/admin/sources', end: false, label: 'Workshop sources', hint: 'Epic 2' },
  { to: '/admin/cohorts', end: false, label: 'Cohorts', hint: 'Epic 3' },
  { to: '/admin/audit', end: false, label: 'Audit log', hint: 'US-6.2' },
] as const;

/**
 * Chrome for the admin panel, plus the *cosmetic* half of BR-AP1: admin
 * navigation is hidden from a non-admin principal. The authoritative check is
 * the Worker's, which denies every /api/admin request without the claim — so a
 * user who routes here directly gets an empty shell and 403s, never data.
 */
export function AdminLayout() {
  const { state } = useIdentity();
  const principal = usePrincipal();

  if (state.status === 'loading') return <Spinner label="Checking your access" />;

  if (!isAdmin(principal)) {
    return (
      <EmptyState
        title="Access denied"
        description="This area is limited to workshop administrators and platform owners. Ask a platform owner to review your role assignment if you think this is wrong."
      />
    );
  }

  return (
    <div className={styles.layout}>
      <nav className={styles.nav} aria-label="Admin sections">
        <p className={styles.label}>Admin panel</p>
        <ul>
          {SECTIONS.map((section) => (
            <li key={section.to}>
              <NavLink
                to={section.to}
                end={section.end}
                className={({ isActive }) => (isActive ? styles.active : styles.link)}
              >
                <span>{section.label}</span>
                <em className={styles.hint}>{section.hint}</em>
              </NavLink>
            </li>
          ))}
        </ul>
        <div className={styles.roles}>
          {principal?.kind === 'staff' &&
            principal.roles.map((role) => (
              <Badge key={role} tone="positive">
                {role.replace(/-/g, ' ')}
              </Badge>
            ))}
        </div>
      </nav>

      <div className={styles.content}>
        <Outlet />
      </div>
    </div>
  );
}
